import { useClient } from 'jsonapi-react';
import { useCallback, useContext, useMemo, useState } from 'react';
import { finalize, from, map, switchMap, tap, throwError } from 'rxjs';

import { JsonApiAuthContext } from '../components/jsonapi-provider';
import entityManager from '../entity-manager';
import { type AtomicOperationExecutor, type AtomicResult } from '../types';

const deserializeResult = (
  result: AtomicResult,
  _index: number,
  _results: AtomicResult[]
): AtomicResult => {
  if (!result.data || result.error) return result;
  return { ...entityManager.deserialize(result), meta: result.meta };
};

const useAtomicOperations = (): { operate: AtomicOperationExecutor; isLoading: boolean } => {
  const { authHeader } = useContext(JsonApiAuthContext);
  const [isLoading, setIsLoading] = useState(false);
  const client = useClient();

  const operate: AtomicOperationExecutor = useCallback(
    (operations) => {
      setIsLoading(true);
      return from(
        fetch(new URL(process.env.REACT_APP_API_BASE_URL + '/operations'), {
          body: JSON.stringify({
            'atomic:operations': operations.map((operation) => operation.serialize(entityManager)),
          }),
          method: 'POST',
          headers: {
            Authorization: authHeader ?? '',
            accept: 'application/vnd.api+json;ext="https://jsonapi.org/ext/atomic"',
            'Content-Type': 'application/vnd.api+json;ext="https://jsonapi.org/ext/atomic"',
          },
        })
      ).pipe(
        finalize(() => {
          setIsLoading(false);
        }),
        switchMap((response) =>
          response.ok
            ? from(response.status === 204 ? Promise.resolve({}) : response.json()).pipe(
                map((json) => json['atomic:results'].map(deserializeResult)),
                tap(() => {
                  client.clearCache();
                })
              )
            : from(response.json()).pipe(
                switchMap((json: any) =>
                  throwError(() =>
                    Array.isArray(json)
                      ? new Error(
                          json.find(
                            (err) =>
                              ![
                                'Subsequent operation failed.',
                                'Operation not executed. Terminated by earlier failure.',
                              ].includes(err.errors?.[0]?.detail)
                          )?.errors?.[0]?.detail ?? 'Server is unavailable'
                        )
                      : new Error(
                          json.errors?.find(
                            (err: { code: string; detail: string }) =>
                              ![
                                'Subsequent operation failed.',
                                'Operation not executed. Terminated by earlier failure.',
                              ].includes(err.detail)
                          )?.detail ?? 'Server is unavailable'
                        )
                  )
                )
              )
        )
      );
    },
    [authHeader]
  );

  return useMemo(() => ({ operate, isLoading }), [operate, isLoading]);
};
export default useAtomicOperations;
